import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { ChevronRightIcon, ChevronLeftIcon, StarIcon, BookIcon, TrendingUpIcon } from 'lucide-react';
type ResultsDashboardProps = {
  onContinue: () => void;
};
export const ResultsDashboard: React.FC<ResultsDashboardProps> = ({
  onContinue
}) => {
  const [activeCareerIndex, setActiveCareerIndex] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const careers = [{
    title: 'UX Designer',
    match: 92,
    description: 'Your creative intuition and analytical mindset make you perfect for designing user experiences that delight and inspire.',
    strengths: ['Visual thinking', 'Empathy', 'Problem solving'],
    icon: '🎨',
    traits: {
      creativity: 90,
      analytical: 80,
      communication: 75,
      technical: 70,
      leadership: 60
    },
    skillGaps: ['Prototyping', 'User Research', 'UI Animation'],
    whyMatch: "Your strong visual thinking abilities and natural empathy allow you to understand user needs intuitively. Your personality profile shows a balance between creativity and analytical thinking that's ideal for UX design."
  }, {
    title: 'Data Scientist',
    match: 87,
    description: 'Your natural pattern recognition abilities and logical approach are ideal for extracting insights from complex data.',
    strengths: ['Analytical thinking', 'Mathematical aptitude', 'Communication'],
    icon: '📊',
    traits: {
      creativity: 65,
      analytical: 95,
      communication: 70,
      technical: 85,
      leadership: 60
    },
    skillGaps: ['Python Programming', 'Machine Learning', 'Data Visualization'],
    whyMatch: 'Your astrological chart indicates strong analytical abilities, and your MBTI results show a preference for logical decision making. Your natural curiosity and pattern recognition skills are perfect for data science.'
  }, {
    title: 'Product Manager',
    match: 84,
    description: 'Your leadership qualities and strategic vision make you well-suited to guiding product development and innovation.',
    strengths: ['Strategic planning', 'Team coordination', 'User advocacy'],
    icon: '🚀',
    traits: {
      creativity: 80,
      analytical: 85,
      communication: 90,
      technical: 65,
      leadership: 85
    },
    skillGaps: ['Agile Methodology', 'Market Analysis', 'Roadmap Planning'],
    whyMatch: 'Your personality assessment reveals strong leadership traits and excellent communication skills. Your ability to balance creative thinking with analytical reasoning makes you naturally suited to product management roles.'
  }];
  const activeCareer = careers[activeCareerIndex];
  const nextCareer = () => {
    if (activeCareerIndex < careers.length - 1) {
      setActiveCareerIndex(activeCareerIndex + 1);
      setShowDetail(false);
    }
  };
  const prevCareer = () => {
    if (activeCareerIndex > 0) {
      setActiveCareerIndex(activeCareerIndex - 1);
      setShowDetail(false);
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-1237">
      <div className="mb-8 text-center" data-id="element-1238">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-1239">
          Your Cosmic Career Matches
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-1240">
          Based on your unique cosmic profile, these careers align with your
          stars
        </p>
      </div>
      <div className="relative" data-id="element-1241">
        {/* Career cards carousel */}
        <div className="flex justify-center mb-6" data-id="element-1242">
          <button onClick={prevCareer} disabled={activeCareerIndex === 0} className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-[#1C2541] rounded-full p-2 ${activeCareerIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1243">
            <ChevronLeftIcon size={24} data-id="element-1244" />
          </button>
          <div className="w-full max-w-md" data-id="element-1245">
            <motion.div key={activeCareerIndex} initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -50
          }} transition={{
            duration: 0.3
          }} data-id="element-1246">
              <Card className="relative overflow-hidden" data-id="element-1247">
                {/* Cosmic background effect */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#7D56F4]/20 to-transparent rounded-full -mr-10 -mt-10" data-id="element-1248"></div>
                <div className="flex items-start" data-id="element-1249">
                  <div className="text-4xl mr-4" data-id="element-1250">{activeCareer.icon}</div>
                  <div className="flex-1" data-id="element-1251">
                    <h3 className="text-xl font-bold mb-1" data-id="element-1252">
                      {activeCareer.title}
                    </h3>
                    <div className="flex items-center mb-3" data-id="element-1253">
                      <div className="w-full bg-[#1C2541] rounded-full h-2 mr-2" data-id="element-1254">
                        <motion.div className="h-2 rounded-full bg-gradient-to-r from-[#7D56F4] to-[#19D3DA]" initial={{
                        width: 0
                      }} animate={{
                        width: `${activeCareer.match}%`
                      }} transition={{
                        duration: 1
                      }} data-id="element-1255"></motion.div>
                      </div>
                      <span className="text-lg font-bold text-[#19D3DA]" data-id="element-1256">
                        {activeCareer.match}%
                      </span>
                    </div>
                    <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-1257">
                      {activeCareer.description}
                    </p>
                    <div className="mb-2 text-sm font-medium" data-id="element-1258">
                      Key Cosmic Strengths:
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1259">
                      {activeCareer.strengths.map((strength, i) => <div key={i} className="bg-[#1C2541] rounded-full px-3 py-1 text-xs flex items-center" data-id="element-1260">
                          <StarIcon size={12} className="text-[#FFD700] mr-1" data-id="element-1261" />
                          {strength}
                        </div>)}
                    </div>
                    <button onClick={() => setShowDetail(!showDetail)} className="mt-4 text-[#7D56F4] text-sm font-medium flex items-center" data-id="element-1262">
                      {showDetail ? 'Hide Details' : 'View Career Details'}
                      <ChevronRightIcon size={16} className={`ml-1 transition-transform ${showDetail ? 'transform rotate-90' : ''}`} data-id="element-1263" />
                    </button>
                  </div>
                </div>
                {showDetail && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="mt-6 pt-6 border-t border-[#3A506B]" data-id="element-1264">
                    <div className="mb-6" data-id="element-1265">
                      <h4 className="font-medium mb-3" data-id="element-1266">Career Genome Match</h4>
                      <div className="space-y-3" data-id="element-1267">
                        {Object.entries(activeCareer.traits).map(([trait, score]) => <div key={trait} className="flex items-center" data-id="element-1268">
                              <span className="text-sm w-32 capitalize" data-id="element-1269">
                                {trait}
                              </span>
                              <div className="flex-1" data-id="element-1270">
                                <div className="h-2 bg-[#1C2541] rounded-full" data-id="element-1271">
                                  <motion.div className="h-2 bg-[#7D56F4] rounded-full" initial={{
                            width: 0
                          }} animate={{
                            width: `${score}%`
                          }} transition={{
                            duration: 0.8
                          }} data-id="element-1272"></motion.div>
                                </div>
                              </div>
                              <span className="text-sm w-8 text-right" data-id="element-1273">
                                {score}%
                              </span>
                            </div>)}
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-1274">
                      <h4 className="font-medium mb-3 flex items-center" data-id="element-1275">
                        <BookIcon size={16} className="mr-2 text-[#19D3DA]" data-id="element-1276" />
                        Why This Fits You
                      </h4>
                      <p className="text-sm text-[#B8C0E0]" data-id="element-1277">
                        {activeCareer.whyMatch}
                      </p>
                    </div>
                    <div data-id="element-1278">
                      <h4 className="font-medium mb-3 flex items-center" data-id="element-1279">
                        <TrendingUpIcon size={16} className="mr-2 text-[#19D3DA]" data-id="element-1280" />
                        Suggested Skill Development
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-1281">
                        {activeCareer.skillGaps.map((skill, i) => <div key={i} className="bg-[#1C2541] rounded-full px-3 py-1 text-xs" data-id="element-1282">
                            {skill}
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}
              </Card>
            </motion.div>
          </div>
          <button onClick={nextCareer} disabled={activeCareerIndex === careers.length - 1} className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-[#1C2541] rounded-full p-2 ${activeCareerIndex === careers.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1283">
            <ChevronRightIcon size={24} data-id="element-1284" />
          </button>
        </div>
        {/* Carousel indicator dots */}
        <div className="flex justify-center space-x-2 mb-8" data-id="element-1285">
          {careers.map((_, index) => <button key={index} onClick={() => {
          setActiveCareerIndex(index);
          setShowDetail(false);
        }} className={`w-2 h-2 rounded-full ${index === activeCareerIndex ? 'bg-[#7D56F4]' : 'bg-[#3A506B]'}`} data-id="element-1286" />)}
        </div>
      </div>
      <div className="mt-8 flex justify-center" data-id="element-1287">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={onContinue} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full" data-id="element-1288">
          Explore Your Career Journey
        </motion.button>
      </div>
    </motion.div>;
};